import { Injectable } from '@angular/core';
import { UserAuth } from 'services/auth.service';
import { CoachManagingClient, UserDb } from 'services/user-db.service';
import { CoachPresenceService } from 'services/coach-presence.service';
import { CoachPresenceState } from 'services/coach-presence-state';

/**
 * Coach/client "who is online" presence as the app shell sees it: whether the
 * managed client is online (a dot on the coach banner) and whether the coach is
 * currently watching (the floating "coach viewing" icon).
 *
 * It decides which side this device is acting as; the separate
 * `CoachPresenceService` does the actual heartbeat writing and document
 * watching. The design is in design-docs/coach-mode.md, "Presence / Who's
 * Online".
 *
 * Lifted out of `AppComponent` with no behavior change. That component still owns
 * WHEN presence is set up and torn down; design-docs/coach-mode.md lists those
 * moments under "Presence / Who's Online".
 */
@Injectable({ providedIn: 'root' })
export class CoachPresenceStateService extends CoachPresenceState {
  constructor(
    presence: CoachPresenceService,
    private userDb: UserDb,
  ) {
    super(presence);
  }

  /**
   * Start presence for the signed-in user. `coachManagingClient` is passed in
   * rather than read here because the app shell already holds it — it is the
   * same value the coach banner renders from, and reading it again could
   * disagree with what the screen is showing.
   */
  setupForUser(user: UserAuth, coachManagingClient: CoachManagingClient | null): void {
    this.setup(user, {
      isManagingClient: () => !!coachManagingClient,
      hasCoach: () => !!this.userDb.loadCoachRelAsClient()?.coachUid,
    });
  }
}
